/**
 * ラボ 1 の盤面（docs/curriculum/labs.md ラボ 1、components.md §3.2 StackLab）。
 * 変数名は `stack`、型は `[Int]`。配列の末尾が山のいちばん上。
 */
import type { ApplyResult, LabModel } from './types';
import { swiftArray, swiftOptional } from './swift-format';

export interface StackState {
  /** 添字 0 が底、末尾が一番上。 */
  readonly items: readonly number[];
}

export type StackOp =
  { type: 'push'; value: number } | { type: 'pop' } | { type: 'peek' } | { type: 'isEmpty' };

export const STACK_LABEL = 'stack';
export const EMPTY_STACK: StackState = { items: [] };

function apply(state: StackState, op: StackOp): ApplyResult<StackState> {
  const { items } = state;
  const top = items.at(-1);
  switch (op.type) {
    case 'push':
      return {
        state: { items: [...items, op.value] },
        entry: { code: `stack.append(${op.value})`, tag: '追加', changed: [STACK_LABEL] },
        moved: 1,
        message: `${op.value} を一番上に積みました。`,
      };
    case 'pop':
      if (top === undefined) {
        return {
          state,
          entry: { code: 'stack.popLast()', result: 'nil', tag: '取り出し', changed: [] },
          moved: 0,
          message: '空の配列の popLast() は nil を返します。盤面は変わりません。',
        };
      }
      return {
        state: { items: items.slice(0, -1) },
        entry: {
          code: 'stack.popLast()',
          result: swiftOptional(top),
          tag: '取り出し',
          changed: [STACK_LABEL],
        },
        moved: 1,
        message: `一番上の ${top} を取り出しました。`,
      };
    case 'peek': {
      const r = swiftOptional(top);
      return {
        state,
        entry: { code: 'print(stack.last)', result: r, stdout: r, tag: '確認', changed: [] },
        moved: 0,
        message:
          top === undefined
            ? '空なので last は nil です。'
            : `一番上は ${top} です。取り出してはいません。`,
      };
    }
    case 'isEmpty': {
      const r = String(items.length === 0);
      return {
        state,
        entry: { code: 'print(stack.isEmpty)', result: r, stdout: r, tag: '確認', changed: [] },
        moved: 0,
        message: items.length === 0 ? '空です（true）。' : `${items.length} 個あります（false）。`,
      };
    }
  }
}

export const stackModel: LabModel<StackState, StackOp> = {
  id: 'stack',
  apply,
  declarations: (initial) => [
    initial.items.length === 0
      ? 'var stack: [Int] = []'
      : `var stack: [Int] = ${swiftArray(initial.items)}`,
  ],
  labels: () => [STACK_LABEL],
  vizLine: () => 'viz.stack("stack", stack)',
  snapshot: (state) => ({ kind: 'stack', value: [...state.items] }),
};
